import { Toaster as BaseToaster, toast as sonnerToast } from 'sonner';
import { Icon, IconName } from './icon';
import { Loading } from './loading';
import { v4 } from 'uuid';
import { omit } from 'lodash';
import { Button } from '@cotera/client/v0/components/ui/button';

const DEFAULT_DURATION = 4000;

export type LoadingToastController = {
  done: () => void;
  error: (message: titleT, data?: ExternalToast) => void;
  success: (message: titleT, data?: ExternalToast) => void;
  update: (
    message: titleT,
    data?: ExternalToast & { variant?: ToastVariant }
  ) => void;
};

function baseToast(toast: Omit<ToastProps, 'id'> & { id?: string | number }) {
  const toastId = toast.id ?? v4();
  sonnerToast.custom(() => <Toast {...toast} id={toastId} />, {
    duration: toast.duration,
    id: toastId,
  });

  return toastId;
}

/** A fully custom toast that still maintains the animations and interactions. */
function Toast(props: ToastProps) {
  const { title, description, button, id, variant } = props;

  const icon: IconName = (() => {
    switch (variant) {
      case 'info':
        return 'information-circle';
      case 'error':
        return 'exclamation-circle';
      case 'success':
        return 'check-circle';
      case 'warning':
        return 'exclamation-triangle';
      default:
        return 'information-circle';
    }
  })();

  const iconClassName = (() => {
    switch (variant) {
      case 'error':
        return 'text-destructive';
      case 'success':
        // Our green success var — not `--chart-2`, which the v1 (Coco) theme
        // overrides to peach, turning the success check orange.
        return 'text-success';
      case 'warning':
        return 'text-destructive';
      default:
        return '';
    }
  })();

  return (
    <div className="flex-col rounded-lg bg-white shadow-md ring-1 ring-black/5 w-full md:max-w-[364px] items-center p-4 pr-6 gap-2 items-center">
      <div className="flex items-center gap-2">
        <div className="shrink-0">
          {variant !== 'loading' && (
            <Icon icon={icon} className={iconClassName} />
          )}
          {variant === 'loading' && <Loading.Spinner variant="sm" />}
        </div>
        <div className="flex flex-1 items-center">
          <div className="w-full">
            <p className="text-sm font-medium text-gray-900">
              {title as React.ReactNode}
            </p>
            {description !== undefined && (
              <p className="mt-1 text-sm text-gray-500">
                {description as React.ReactNode}
              </p>
            )}
          </div>
        </div>
        {button && (
          <div className="ml-5 shrink-0 rounded-md text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden">
            <button
              className="rounded bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-600 hover:bg-indigo-100"
              onClick={() => {
                button.onClick();
                sonnerToast.dismiss(id);
              }}
            >
              {button.label}
            </button>
          </div>
        )}
        <div className="ml-3 shrink-0">
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => {
              sonnerToast.dismiss(id);
            }}
            aria-label="Dismiss notification"
          >
            <Icon icon="x-mark" size="regular" />
          </Button>
        </div>
      </div>
    </div>
  );
}

type ToastVariant = 'info' | 'error' | 'success' | 'warning' | 'loading';

type ToastProps = {
  id: string | number;
  title: titleT;
  description?: titleT;
  variant?: ToastVariant;
  duration?: number;
  button?: {
    label: string;
    onClick: () => void;
  };
};

type ExternalToast = Omit<
  ToastProps,
  'id' | 'type' | 'title' | 'jsx' | 'delete' | 'promise'
> & { id?: string | number };

type titleT = React.ReactNode | (() => React.ReactNode);

export type Toaster = typeof toast;

export const toast = {
  info: (message: titleT, data?: ExternalToast) =>
    baseToast({
      title: message,
      variant: 'info',
      ...data,
    }),
  error: (message: titleT, data?: ExternalToast) =>
    baseToast({
      title: message,
      variant: 'error',
      ...data,
    }),
  success: (message: titleT, data?: ExternalToast) =>
    baseToast({
      title: message,
      variant: 'success',
      ...data,
    }),
  warning: (message: titleT, data?: ExternalToast) =>
    baseToast({
      title: message,
      variant: 'warning',
      ...data,
    }),
  noop: (_: titleT, __?: ExternalToast): LoadingToastController => {
    return {
      success: () => {},
      error: () => {},
      done: () => {},
      update: () => {},
    };
  },
  loading: (message: titleT, data?: ExternalToast): LoadingToastController => {
    const id = baseToast({
      variant: 'loading',
      title: message,
      duration: Infinity,
      ...data,
    });

    const update = (
      message: titleT,
      data?: ExternalToast & { variant?: ToastVariant }
    ) => {
      baseToast({
        id,
        duration: DEFAULT_DURATION,
        title: message,
        ...omit(data, 'duration', 'id'),
      });
    };

    const loadingToastController: LoadingToastController = {
      done: () => sonnerToast.dismiss(id),
      error: (message: titleT, data?: ExternalToast) => {
        update(message, {
          ...data,
          variant: 'error',
        });
      },
      success: (message: titleT, data?: ExternalToast) => {
        update(message, {
          ...data,
          variant: 'success',
        });
      },
      update,
    };

    return loadingToastController;
  },
};

export const Toaster: React.FC = () => (
  <BaseToaster visibleToasts={9} position="top-right" />
);
